<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import ticketsApi from 'dashboard/api/tickets';
import ticketTypesApi from 'dashboard/api/ticketTypes';
import ticketCompaniesApi from 'dashboard/api/ticketCompanies';
import TypeTreeNode from './TypeTreeNode.vue';
import TypeFilterNode from './TypeFilterNode.vue';
import Draggable from 'vuedraggable';

const { t } = useI18n();
const store = useStore();

// ─── State ───────────────────────────────────────────────────────────────────
const tickets = ref([]);
const isLoading = ref(false);
const showModal = ref(false);
const editingTicket = ref(null);
const showDone = ref(false);
const priorityFilter = ref('all');
const statusFilter = ref('active');
const searchQuery = ref('');
const deleteConfirmId = ref(null);
const isSaving = ref(false);
const viewMode = ref('card');
const assigneeFilter = ref([]); // IDs de agentes selecionados
const typeFilter = ref(null); // ID do tipo selecionado
const showAssigneeDropdown = ref(false);
const showTypeDropdown = ref(false);

// ─── Ticket Types state ───────────────────────────────────────────────────────
const ticketTypes = ref([]); // árvore de tipos (nested)
const showTypeManager = ref(false); // modal de gerenciamento
const typeForm = ref({ name: '', color: '#6366f1', parent_id: null });
const editingType = ref(null);
const typeDeleteConfirmId = ref(null);
const isSavingType = ref(false);
// Seletor cascata no form do ticket
const cascadePath = ref([]); // IDs selecionados em cada nível

// ─── Ticket Companies state ───────────────────────────────────────────────────
const ticketCompanies = ref([]);
const showCompanyManager = ref(false);
const companyForm = ref({ name: '', responsible_name: '' });
const editingCompany = ref(null);
const companyDeleteConfirmId = ref(null);
const isSavingCompany = ref(false);
// Company picker no form do ticket
const showCompanyPicker = ref(false);
const companySearch = ref('');

// ─── Description editor ───────────────────────────────────────────────────────
const descriptionExpanded = ref(false);
const descriptionRef = ref(null);
const descriptionExpandedRef = ref(null);

// ─── Checklist ────────────────────────────────────────────────────────────────
const checklistRefs = ref([]);

// ─── Store ────────────────────────────────────────────────────────────────────
const agents = useMapGetter('agents/getAgents');

// ─── Constants ────────────────────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  urgent: {
    label: 'Urgente',
    color: 'bg-red-800 text-white',
    border: 'border-l-red-800',
    badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  },
  high: {
    label: 'Alta',
    color: 'bg-orange-500 text-white',
    border: 'border-l-orange-500',
    badge:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  },
  medium: {
    label: 'Média',
    color: 'bg-yellow-400 text-yellow-900',
    border: 'border-l-yellow-400',
    badge:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  },
  low: {
    label: 'Baixa',
    color: 'bg-slate-400 text-white',
    border: 'border-l-slate-400',
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  },
};

const STATUS_CONFIG = {
  open: {
    label: 'Aberto',
    badge: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
  in_progress: {
    label: 'Em andamento',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    dot: 'bg-blue-500',
  },
  done: {
    label: 'Concluído',
    badge:
      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
};

const TICKET_TYPES_OPTIONS = [
  { value: 'commercial', label: 'Comercial' },
  { value: 'support', label: 'Suporte' },
  { value: 'financial', label: 'Financeiro' },
  { value: 'development', label: 'Desenvolvimento' },
  { value: 'operational', label: 'Operacional' },
  { value: 'scheduling', label: 'Agendamento' },
  { value: 'service', label: 'Atendimento' },
  { value: 'other', label: 'Outro' },
];

// ─── Ticket Types helpers ─────────────────────────────────────────────────────
// Achata a árvore em mapa id→node
function flattenTypes(nodes, map = {}) {
  for (const n of nodes) {
    map[n.id] = n;
    if (n.children?.length) flattenTypes(n.children, map);
  }
  return map;
}

const typesMap = computed(() => flattenTypes(ticketTypes.value));

// Colunas do seletor cascata: [raízes, filhos do selecionado no nível 0, ...]
const cascadeColumns = computed(() => {
  const cols = [ticketTypes.value]; // nível 0 = raízes
  for (const id of cascadePath.value) {
    const node = typesMap.value[id];
    if (node?.children?.length) cols.push(node.children);
    else break;
  }
  return cols;
});

// ID do tipo selecionado = último da cascadePath
const selectedTypeId = computed(
  () => cascadePath.value[cascadePath.value.length - 1] ?? null
);

// Rótulo do caminho completo para exibição
function typePathLabel(typeId) {
  if (!typeId || !typesMap.value[typeId]) return null;
  const parts = [];
  let node = typesMap.value[typeId];
  while (node) {
    parts.unshift(node.name);
    node = node.parent_id ? typesMap.value[node.parent_id] : null;
  }
  return parts;
}

function cascadeSelect(levelIdx, nodeId) {
  // Seleciona/deseleciona: trunca o path até levelIdx e define o nó
  const newPath = cascadePath.value.slice(0, levelIdx);
  if (cascadePath.value[levelIdx] === nodeId) {
    // Deseleciona ao clicar de novo
    cascadePath.value = newPath;
  } else {
    newPath.push(nodeId);
    cascadePath.value = newPath;
  }
}

function initCascadeFromTypeId(typeId) {
  if (!typeId || !typesMap.value[typeId]) {
    cascadePath.value = [];
    return;
  }
  const path = [];
  let node = typesMap.value[typeId];
  while (node) {
    path.unshift(node.id);
    node = node.parent_id ? typesMap.value[node.parent_id] : null;
  }
  cascadePath.value = path;
}

// ─── Ticket Types CRUD ────────────────────────────────────────────────────────
async function fetchTicketTypes() {
  try {
    const res = await ticketTypesApi.list();
    ticketTypes.value = res.data || [];
  } catch {
    /* silent */
  }
}

function openTypeForm(parentId = null, existing = null) {
  editingType.value = existing;
  typeForm.value = existing
    ? {
        name: existing.name,
        color: existing.color || '#6366f1',
        parent_id: existing.parent_id,
      }
    : { name: '', color: '#6366f1', parent_id: parentId };
}

function closeTypeForm() {
  editingType.value = null;
  typeForm.value = { name: '', color: '#6366f1', parent_id: null };
}

async function saveTicketType() {
  if (!typeForm.value.name.trim()) return;
  isSavingType.value = true;
  try {
    if (editingType.value) {
      await ticketTypesApi.update(editingType.value.id, typeForm.value);
    } else {
      await ticketTypesApi.create(typeForm.value);
    }
    await fetchTicketTypes();
    closeTypeForm();
  } catch {
    /* silent */
  } finally {
    isSavingType.value = false;
  }
}

async function deleteTicketType(id) {
  try {
    await ticketTypesApi.delete(id);
    await fetchTicketTypes();
    typeDeleteConfirmId.value = null;
  } catch {
    /* silent */
  }
}

// ─── Ticket Companies CRUD ────────────────────────────────────────────────────
async function fetchTicketCompanies() {
  try {
    const res = await ticketCompaniesApi.list();
    ticketCompanies.value = res.data || [];
  } catch {
    /* silent */
  }
}

const filteredCompanies = computed(() => {
  const q = companySearch.value.toLowerCase().trim();
  if (!q) return ticketCompanies.value;
  return ticketCompanies.value.filter(
    c =>
      c.name.toLowerCase().includes(q) ||
      (c.responsible_name || '').toLowerCase().includes(q)
  );
});

function openCompanyForm(existing = null) {
  editingCompany.value = existing;
  companyForm.value = existing
    ? { name: existing.name, responsible_name: existing.responsible_name || '' }
    : { name: '', responsible_name: '' };
}

function closeCompanyForm() {
  editingCompany.value = null;
  companyForm.value = { name: '', responsible_name: '' };
}

async function saveCompany() {
  if (!companyForm.value.name.trim()) return;
  isSavingCompany.value = true;
  try {
    if (editingCompany.value) {
      await ticketCompaniesApi.update(
        editingCompany.value.id,
        companyForm.value
      );
    } else {
      await ticketCompaniesApi.create(companyForm.value);
    }
    await fetchTicketCompanies();
    closeCompanyForm();
  } catch {
    /* silent */
  } finally {
    isSavingCompany.value = false;
  }
}

async function deleteCompany(id) {
  try {
    await ticketCompaniesApi.delete(id);
    await fetchTicketCompanies();
    companyDeleteConfirmId.value = null;
  } catch {
    /* silent */
  }
}

function selectCompany(company) {
  form.value.company_name = company.name;
  form.value.contact_name = company.responsible_name || '';
  showCompanyPicker.value = false;
  companySearch.value = '';
}

// ─── Form state ───────────────────────────────────────────────────────────────
const emptyForm = () => ({
  title: '',
  subtitle: '',
  description: '',
  contact_name: '',
  company_name: '',
  status: 'open',
  priority: 'medium',
  ticket_types: [],
  ticket_type_id: null,
  assignee_ids: [],
  due_date: '',
  checklist: [],
});

const form = ref(emptyForm());

// ─── Computed ─────────────────────────────────────────────────────────────────
// Retorna true se o ticket pertence ao tipo selecionado (ou a qualquer descendente)
function ticketMatchesTypeFilter(ticket) {
  if (!typeFilter.value) return true;
  if (!ticket.ticket_type_id) return false;
  // sobe a árvore a partir do tipo do ticket: se algum nó == filtro, match
  let node = typesMap.value[ticket.ticket_type_id];
  while (node) {
    if (node.id === typeFilter.value) return true;
    node = node.parent_id ? typesMap.value[node.parent_id] : null;
  }
  return false;
}

function applyCommonFilters(list) {
  if (assigneeFilter.value.length) {
    list = list.filter(t =>
      assigneeFilter.value.some(id => t.assignee_ids?.includes(id))
    );
  }
  if (typeFilter.value) {
    list = list.filter(ticketMatchesTypeFilter);
  }
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(
      t =>
        t.title.toLowerCase().includes(q) ||
        (t.contact_name || '').toLowerCase().includes(q) ||
        (t.company_name || '').toLowerCase().includes(q)
    );
  }
  return list;
}

const activeTickets = computed(() => {
  let list = tickets.value.filter(t => t.status !== 'done');
  if (priorityFilter.value !== 'all')
    list = list.filter(t => t.priority === priorityFilter.value);
  if (statusFilter.value !== 'active' && statusFilter.value !== 'all')
    list = list.filter(t => t.status === statusFilter.value);
  return applyCommonFilters(list);
});

const doneTickets = computed(() => {
  return applyCommonFilters(tickets.value.filter(t => t.status === 'done'));
});

const activeCount = computed(
  () => tickets.value.filter(t => t.status !== 'done').length
);
const doneCount = computed(
  () => tickets.value.filter(t => t.status === 'done').length
);

const doneChecklistCount = computed(
  () => form.value.checklist.filter(i => i.done).length
);
const checklistProgress = computed(() => {
  if (!form.value.checklist.length) return 0;
  return Math.round(
    (doneChecklistCount.value / form.value.checklist.length) * 100
  );
});
const totalEstimatedMinutes = computed(() =>
  form.value.checklist
    .filter(i => !i.done)
    .reduce((sum, i) => sum + (i.estimated_minutes || 0), 0)
);

function formatMinutes(mins) {
  if (!mins) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

function updateEstimatedTime(item, unit, value) {
  const val = Math.max(0, parseInt(value) || 0);
  const current = item.estimated_minutes || 0;
  if (unit === 'hours') {
    item.estimated_minutes = val * 60 + (current % 60);
  } else {
    item.estimated_minutes = Math.floor(current / 60) * 60 + Math.min(59, val);
  }
}

const countsByPriority = computed(() => {
  const active = tickets.value.filter(t => t.status !== 'done');
  return {
    urgent: active.filter(t => t.priority === 'urgent').length,
    high: active.filter(t => t.priority === 'high').length,
    medium: active.filter(t => t.priority === 'medium').length,
    low: active.filter(t => t.priority === 'low').length,
  };
});

// ─── Due date helpers ─────────────────────────────────────────────────────────
function dueDateInfo(dueDateStr) {
  if (!dueDateStr)
    return { label: 'Sem prazo', classes: 'text-n-slate-10', icon: '' };
  const now = new Date();
  const due = new Date(dueDateStr);
  const diffMs = due - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0)
    return {
      label: 'Vencido',
      classes: 'text-red-600 font-semibold',
      icon: 'i-lucide-alarm-clock',
    };
  if (diffDays === 0)
    return {
      label: 'Vence hoje',
      classes: 'text-orange-600 font-semibold',
      icon: 'i-lucide-alarm-clock',
    };
  if (diffDays === 1)
    return {
      label: 'Vence amanhã',
      classes: 'text-yellow-600 font-medium',
      icon: 'i-lucide-clock',
    };
  if (diffDays <= 3)
    return {
      label: `${diffDays}d restantes`,
      classes: 'text-yellow-600',
      icon: 'i-lucide-clock',
    };
  if (diffDays <= 7)
    return {
      label: `${diffDays}d restantes`,
      classes: 'text-n-slate-11',
      icon: 'i-lucide-clock',
    };
  return {
    label: formatDate(dueDateStr),
    classes: 'text-n-slate-10',
    icon: 'i-lucide-calendar',
  };
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Agent helpers ────────────────────────────────────────────────────────────
function getAgent(id) {
  return agents.value.find(a => a.id === id);
}

function agentInitials(agent) {
  if (!agent) return '?';
  return agent.name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0])
    .join('')
    .toUpperCase();
}

function agentFirstName(agent) {
  if (!agent) return '';
  return agent.name.split(' ')[0];
}

const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-blue-500',
  'bg-emerald-500',
  'bg-orange-500',
  'bg-rose-500',
  'bg-indigo-500',
];
function avatarColor(id) {
  return AVATAR_COLORS[id % AVATAR_COLORS.length];
}

// ─── API actions ──────────────────────────────────────────────────────────────
async function fetchTickets() {
  isLoading.value = true;
  try {
    const res = await ticketsApi.list();
    tickets.value = res.data?.payload || res.data || [];
  } catch {
    // silent
  } finally {
    isLoading.value = false;
  }
}

function openCreateModal() {
  editingTicket.value = null;
  form.value = emptyForm();
  showModal.value = true;
}

function openEditModal(ticket) {
  editingTicket.value = ticket;
  form.value = {
    title: ticket.title || '',
    subtitle: ticket.subtitle || '',
    description: ticket.description || '',
    contact_name: ticket.contact_name || '',
    company_name: ticket.company_name || '',
    status: ticket.status || 'open',
    priority: ticket.priority || 'medium',
    ticket_types: [...(ticket.ticket_types || [])],
    ticket_type_id: ticket.ticket_type_id || null,
    assignee_ids: [...(ticket.assignee_ids || [])],
    due_date: ticket.due_date ? ticket.due_date.substring(0, 10) : '',
    checklist: (ticket.checklist || []).map(i => ({
      ...i,
      estimated_minutes: i.estimated_minutes || 0,
    })),
  };
  initCascadeFromTypeId(ticket.ticket_type_id);
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
  editingTicket.value = null;
  descriptionExpanded.value = false;
}

function autoResizeDescription() {
  const el = descriptionRef.value;
  if (!el) return;
  el.style.height = 'auto';
  const maxH = 200;
  el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
  el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
}

async function openDescriptionEditor() {
  descriptionExpanded.value = true;
  await nextTick();
  descriptionExpandedRef.value?.focus();
}

async function saveTicket() {
  if (!form.value.title.trim()) return;
  isSaving.value = true;
  try {
    const payload = {
      ...form.value,
      due_date: form.value.due_date || null,
      ticket_type_id: selectedTypeId.value,
    };
    if (editingTicket.value) {
      const res = await ticketsApi.update(editingTicket.value.id, payload);
      const updated = res.data;
      const idx = tickets.value.findIndex(t => t.id === updated.id);
      if (idx !== -1) tickets.value[idx] = updated;
    } else {
      const res = await ticketsApi.create(payload);
      tickets.value.unshift(res.data);
    }
    closeModal();
  } catch {
    // silent
  } finally {
    isSaving.value = false;
  }
}

async function quickStatusChange(ticket, newStatus) {
  try {
    const res = await ticketsApi.update(ticket.id, { status: newStatus });
    const updated = res.data;
    const idx = tickets.value.findIndex(t => t.id === updated.id);
    if (idx !== -1) tickets.value[idx] = updated;
  } catch {
    // silent
  }
}

async function deleteTicket(id) {
  try {
    await ticketsApi.delete(id);
    tickets.value = tickets.value.filter(t => t.id !== id);
    deleteConfirmId.value = null;
  } catch {
    // silent
  }
}

async function handleActiveReorder(event) {
  const { moved } = event;
  if (!moved) return;

  const currentList = [...activeTickets.value];
  const [item] = currentList.splice(moved.oldIndex, 1);
  currentList.splice(moved.newIndex, 0, item);

  const visibleIds = new Set(activeTickets.value.map(t => t.id));
  const rest = tickets.value.filter(t => !visibleIds.has(t.id));
  tickets.value = [...currentList, ...rest];

  try {
    await ticketsApi.reorder(currentList.map(t => t.id));
  } catch {
    await fetchTickets();
  }
}

async function handleDoneReorder(event) {
  const { moved } = event;
  if (!moved) return;

  const currentList = [...doneTickets.value];
  const [item] = currentList.splice(moved.oldIndex, 1);
  currentList.splice(moved.newIndex, 0, item);

  const visibleIds = new Set(doneTickets.value.map(t => t.id));
  const rest = tickets.value.filter(t => !visibleIds.has(t.id));
  tickets.value = [...rest, ...currentList];

  try {
    await ticketsApi.reorder(currentList.map(t => t.id));
  } catch {
    await fetchTickets();
  }
}

function toggleType(type) {
  const idx = form.value.ticket_types.indexOf(type);
  if (idx === -1) form.value.ticket_types.push(type);
  else form.value.ticket_types.splice(idx, 1);
}

function toggleAssignee(id) {
  const idx = form.value.assignee_ids.indexOf(id);
  if (idx === -1) form.value.assignee_ids.push(id);
  else form.value.assignee_ids.splice(idx, 1);
}

// ─── Checklist helpers (per-ticket, for card/list views) ──────────────────────
function ticketEstimatedMinutes(ticket) {
  return (ticket.checklist || [])
    .filter(i => !i.done)
    .reduce((sum, i) => sum + (i.estimated_minutes || 0), 0);
}

function ticketChecklistDone(ticket) {
  return (ticket.checklist || []).filter(i => i.done).length;
}

function ticketChecklistProgress(ticket) {
  if (!ticket.checklist?.length) return 0;
  return Math.round(
    (ticketChecklistDone(ticket) / ticket.checklist.length) * 100
  );
}

// ─── Checklist actions ─────────────────────────────────────────────────────────
function newChecklistItem() {
  return {
    id: `ck_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    text: '',
    done: false,
    estimated_minutes: 0,
  };
}

function addChecklistItem() {
  form.value.checklist.push(newChecklistItem());
  nextTick(() => {
    const idx = form.value.checklist.length - 1;
    checklistRefs.value[idx]?.focus();
  });
}

function addChecklistItemAfter(idx) {
  form.value.checklist.splice(idx + 1, 0, newChecklistItem());
  nextTick(() => {
    checklistRefs.value[idx + 1]?.focus();
  });
}

function removeChecklistItem(idx) {
  form.value.checklist.splice(idx, 1);
}

function handleChecklistBackspace(event, idx) {
  if (form.value.checklist[idx]?.text === '') {
    event.preventDefault();
    removeChecklistItem(idx);
    nextTick(() => {
      checklistRefs.value[Math.max(0, idx - 1)]?.focus();
    });
  }
}

// ─── Lifecycle ─────────────────────────────────────────────────────────────────
function closeDropdowns(e) {
  if (!e.target.closest('[data-dropdown]')) {
    showAssigneeDropdown.value = false;
    showTypeDropdown.value = false;
  }
  if (!e.target.closest('[data-company-picker]')) {
    showCompanyPicker.value = false;
  }
}

onMounted(() => {
  fetchTickets();
  fetchTicketTypes();
  fetchTicketCompanies();
  store.dispatch('agents/get');
  document.addEventListener('click', closeDropdowns);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdowns);
});

watch(showModal, async open => {
  if (open) {
    await nextTick();
    autoResizeDescription();
  }
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background overflow-hidden">
    <!-- ── Header ─────────────────────────────────────────────────────────── -->
    <div class="flex-shrink-0 border-b border-n-weak px-6 py-4">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-3">
          <span class="i-lucide-ticket-check size-6 text-n-brand" />
          <div>
            <h1 class="text-lg font-semibold text-n-slate-12">Tickets</h1>
            <p class="text-xs text-n-slate-10">
              {{ activeCount }} {{ activeCount === 1 ? 'ativo' : 'ativos' }}
              <span v-if="doneCount">
                · {{ doneCount }} concluído{{
                  doneCount !== 1 ? 's' : ''
                }}</span>
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            class="flex items-center gap-2 px-3 py-2 border border-n-weak rounded-lg text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="showCompanyManager = true"
          >
            <span class="i-lucide-building-2 size-4" />
            Empresas
          </button>
          <button
            class="flex items-center gap-2 px-3 py-2 border border-n-weak rounded-lg text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="showTypeManager = true"
          >
            <span class="i-lucide-git-branch size-4" />
            Tipos
          </button>
          <button
            class="flex items-center gap-2 px-4 py-2 bg-n-brand text-white rounded-lg text-sm font-medium hover:bg-n-brand/90 transition-colors"
            @click="openCreateModal"
          >
            <span class="i-lucide-plus size-4" />
            Novo Ticket
          </button>
        </div>
      </div>

      <!-- ── Filter bar ──────────────────────────────────────────────────── -->
      <div class="mt-4 flex flex-wrap items-center gap-2">
        <!-- Search -->
        <div class="relative flex-1 min-w-[180px] max-w-xs">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-9"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar tickets..."
            class="w-full pl-8 pr-3 py-1.5 text-sm bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-9"
          />
        </div>

        <!-- Mostrar todos -->
        <button
          class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
          :class="
            priorityFilter === 'all'
              ? 'bg-n-brand text-white border-n-brand'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="priorityFilter = 'all'"
        >
          Mostrar Todos
        </button>

        <!-- Filtro por Responsável -->
        <div class="relative" data-dropdown>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
            :class="
              assigneeFilter.length
                ? 'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-700'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="
              showAssigneeDropdown = !showAssigneeDropdown;
              showTypeDropdown = false;
            "
          >
            <span class="i-lucide-users size-3.5" />
            Responsável
            <span
              v-if="assigneeFilter.length"
              class="ml-0.5 size-4 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center"
            >
              {{ assigneeFilter.length }}
            </span>
            <span
              class="i-lucide-chevron-down size-3 opacity-60"
              :class="{ 'rotate-180': showAssigneeDropdown }"
            />
          </button>

          <!-- Dropdown agentes -->
          <div
            v-if="showAssigneeDropdown"
            class="absolute top-full left-0 mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl min-w-[200px] py-1.5 overflow-hidden"
          >
            <div
              class="px-3 py-1.5 border-b border-n-weak flex items-center justify-between"
            >
              <span
                class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wide"
                >Responsáveis</span>
              <button
                v-if="assigneeFilter.length"
                class="text-[10px] text-n-brand hover:underline"
                @click="assigneeFilter = []"
              >
                Limpar
              </button>
            </div>
            <div class="max-h-52 overflow-y-auto">
              <button
                v-for="agent in agents"
                :key="agent.id"
                class="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-n-alpha-1 transition-colors text-left"
                @click="
                  assigneeFilter.includes(agent.id)
                    ? (assigneeFilter = assigneeFilter.filter(
                        id => id !== agent.id
                      ))
                    : assigneeFilter.push(agent.id)
                "
              >
                <div
                  class="size-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                  :class="avatarColor(agent.id)"
                >
                  {{ agentInitials(agent) }}
                </div>
                <span class="flex-1 text-xs text-n-slate-12 truncate">{{
                  agent.name
                }}</span>
                <span
                  class="size-4 rounded flex items-center justify-center flex-shrink-0 transition-colors"
                  :class="
                    assigneeFilter.includes(agent.id)
                      ? 'bg-violet-600 text-white'
                      : 'border border-n-weak'
                  "
                >
                  <span
                    v-if="assigneeFilter.includes(agent.id)"
                    class="i-lucide-check size-2.5"
                  />
                </span>
              </button>
              <p
                v-if="!agents.length"
                class="px-3 py-3 text-xs text-n-slate-8 text-center"
              >
                Nenhum agente
              </p>
            </div>
          </div>
        </div>

        <!-- Filtro por Tipo -->
        <div class="relative" data-dropdown>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
            :class="
              typeFilter
                ? 'bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="
              showTypeDropdown = !showTypeDropdown;
              showAssigneeDropdown = false;
            "
          >
            <span class="i-lucide-git-branch size-3.5" />
            <span v-if="typeFilter && typesMap[typeFilter]">{{
              typesMap[typeFilter].name
            }}</span>
            <span v-else>Tipo</span>
            <span
              v-if="typeFilter"
              class="i-lucide-x size-3 ml-0.5 hover:text-red-500"
              @click.stop="
                typeFilter = null;
                showTypeDropdown = false;
              "
            />
            <span
              v-else
              class="i-lucide-chevron-down size-3 opacity-60"
              :class="{ 'rotate-180': showTypeDropdown }"
            />
          </button>

          <!-- Dropdown tipos (árvore flat com indentação) -->
          <div
            v-if="showTypeDropdown"
            class="absolute top-full left-0 mt-1 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl min-w-[220px] py-1.5 overflow-hidden"
          >
            <div
              class="px-3 py-1.5 border-b border-n-weak flex items-center justify-between"
            >
              <span
                class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wide"
                >Filtrar por Tipo</span>
              <button
                v-if="typeFilter"
                class="text-[10px] text-n-brand hover:underline"
                @click="typeFilter = null"
              >
                Limpar
              </button>
            </div>
            <div class="max-h-64 overflow-y-auto">
              <template v-if="!ticketTypes.length">
                <p class="px-3 py-3 text-xs text-n-slate-8 text-center">
                  Nenhum tipo cadastrado
                </p>
              </template>
              <TypeFilterNode
                v-for="node in ticketTypes"
                :key="node.id"
                :node="node"
                :depth="0"
                :selected-id="typeFilter"
                @select="
                  typeFilter = $event;
                  showTypeDropdown = false;
                "
              />
            </div>
          </div>
        </div>

        <!-- Status filter + View mode toggle (agrupados à direita) -->
        <div class="flex items-center gap-1 ml-auto">
          <!-- Status filters com cores -->
          <button
            class="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
            :class="
              statusFilter === 'active'
                ? 'bg-n-slate-12 text-n-background border-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="statusFilter = 'active'"
          >
            Ativos
          </button>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
            :class="
              statusFilter === 'open'
                ? 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800'
                : 'border-n-weak text-n-slate-11 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 dark:hover:bg-rose-900/20 dark:hover:text-rose-300'
            "
            @click="statusFilter = 'open'"
          >
            <span class="size-1.5 rounded-full bg-rose-500 flex-shrink-0" />
            Aberto
          </button>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border"
            :class="
              statusFilter === 'in_progress'
                ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800'
                : 'border-n-weak text-n-slate-11 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 dark:hover:bg-blue-900/20 dark:hover:text-blue-300'
            "
            @click="statusFilter = 'in_progress'"
          >
            <span class="size-1.5 rounded-full bg-blue-500 flex-shrink-0" />
            Em andamento
          </button>

          <!-- Divisor -->
          <span class="w-px h-5 bg-n-weak mx-1" />

          <!-- Modo card/lista -->
          <button
            class="p-1.5 rounded-lg border transition-colors"
            :class="
              viewMode === 'card'
                ? 'bg-n-brand text-white border-n-brand'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            title="Modo Card"
            @click="viewMode = 'card'"
          >
            <span class="i-lucide-layout-grid size-3.5" />
          </button>
          <button
            class="p-1.5 rounded-lg border transition-colors"
            :class="
              viewMode === 'list'
                ? 'bg-n-brand text-white border-n-brand'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
            title="Modo Lista"
            @click="viewMode = 'list'"
          >
            <span class="i-lucide-list size-3.5" />
          </button>
        </div>
      </div>
    </div>

    <!-- ── Body ───────────────────────────────────────────────────────────── -->
    <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <div class="flex flex-col items-center gap-3 text-n-slate-9">
          <span class="i-lucide-loader-circle size-8 animate-spin" />
          <span class="text-sm">Carregando tickets...</span>
        </div>
      </div>

      <template v-else>
        <!-- ── Priority summary cards ─────────────────────────────────── -->
        <div class="flex gap-2">
          <button
            v-for="p in ['urgent', 'high', 'medium', 'low']"
            :key="p"
            class="flex-1 flex items-center gap-2.5 px-3 py-2.5 bg-n-alpha-1 border border-n-weak rounded-lg border-l-4 transition-all text-left"
            :class="[
              {
                'border-l-red-800': p === 'urgent',
                'border-l-orange-500': p === 'high',
                'border-l-yellow-400': p === 'medium',
                'border-l-slate-400': p === 'low',
              },
              priorityFilter === p
                ? 'bg-n-alpha-2 shadow-md ring-1 ring-inset ring-n-weak'
                : 'hover:bg-n-alpha-2 hover:shadow-sm',
            ]"
            @click="priorityFilter = priorityFilter === p ? 'all' : p"
          >
            <div class="min-w-0 flex-1">
              <p
                class="text-[10px] font-semibold uppercase tracking-wider"
                :class="
                  priorityFilter === p ? 'text-n-slate-12' : 'text-n-slate-9'
                "
              >
                {{ PRIORITY_CONFIG[p].label }}
              </p>
              <p class="text-2xl font-bold text-n-slate-12 leading-tight">
                {{ countsByPriority[p] }}
              </p>
            </div>
            <span
              class="size-2 rounded-full flex-shrink-0 self-start mt-1"
              :class="{
                'bg-red-800': p === 'urgent',
                'bg-orange-500': p === 'high',
                'bg-yellow-400': p === 'medium',
                'bg-slate-400': p === 'low',
              }"
            />
          </button>
        </div>

        <!-- ── Active tickets ──────────────────────────────────────────── -->
        <div>
          <div
            v-if="activeTickets.length === 0"
            class="flex flex-col items-center justify-center py-16 text-center"
          >
            <span class="i-lucide-inbox size-12 text-n-slate-7 mb-4" />
            <p class="text-base font-medium text-n-slate-11">
              Nenhum ticket encontrado
            </p>
            <p class="text-sm text-n-slate-9 mt-1">
              Crie um novo ticket para começar
            </p>
            <button
              class="mt-4 px-4 py-2 bg-n-brand text-white rounded-lg text-sm font-medium hover:bg-n-brand/90 transition-colors"
              @click="openCreateModal"
            >
              Criar ticket
            </button>
          </div>

          <template v-else>
            <!-- ─── Card mode ──────────────────────────────────────────────── -->
            <Draggable
              v-if="viewMode === 'card'"
              :model-value="activeTickets"
              item-key="id"
              class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
              handle=".drag-handle"
              animation="200"
              ghost-class="opacity-40"
              @change="handleActiveReorder"
            >
              <template #item="{ element: ticket }">
                <div
                  class="group relative bg-n-alpha-1 border border-n-weak rounded-2xl border-l-4 hover:shadow-md transition-all duration-150 cursor-pointer"
                  :class="PRIORITY_CONFIG[ticket.priority]?.border"
                  @click="openEditModal(ticket)"
                >
                  <div class="p-3">
                    <!-- Top row: drag handle + contact + status + priority -->
                    <div class="flex items-start justify-between gap-3 mb-1.5">
                      <div class="flex items-start gap-1.5 min-w-0">
                        <span
                          class="drag-handle i-lucide-grip-vertical size-3.5 text-n-slate-7 hover:text-n-slate-10 cursor-grab mt-0.5 flex-shrink-0"
                          title="Arrastar para reordenar"
                          @click.stop
                        />
                        <div class="min-w-0">
                          <p
                            v-if="ticket.contact_name"
                            class="text-xs font-semibold text-n-slate-12 truncate leading-tight"
                          >
                            {{ ticket.contact_name }}
                          </p>
                          <p
                            v-if="ticket.company_name"
                            class="text-[10px] text-n-slate-9 truncate mt-0.5"
                          >
                            <span
                              class="i-lucide-building-2 size-3 mr-0.5 inline-block align-middle"
                            />
                            {{ ticket.company_name }}
                          </p>
                        </div>
                      </div>
                      <div class="flex items-center gap-1.5 flex-shrink-0">
                        <span
                          class="px-2 py-0.5 rounded-full text-[10px] font-medium"
                          :class="STATUS_CONFIG[ticket.status]?.badge"
                        >
                          {{ STATUS_CONFIG[ticket.status]?.label }}
                        </span>
                        <span
                          class="px-2 py-0.5 rounded-full text-[10px] font-medium"
                          :class="PRIORITY_CONFIG[ticket.priority]?.badge"
                        >
                          {{ PRIORITY_CONFIG[ticket.priority]?.label }}
                        </span>
                      </div>
                    </div>
                    <!-- Title -->
                    <h3
                      class="text-xs font-bold text-n-slate-12 uppercase tracking-wide mb-1.5"
                    >
                      {{ ticket.title }}
                    </h3>
                    <!-- Type breadcrumb -->
                    <div
                      v-if="ticket.ticket_type_path?.length"
                      class="flex items-center flex-wrap gap-0.5 mb-1.5"
                    >
                      <template
                        v-for="(seg, i) in ticket.ticket_type_path"
                        :key="i"
                      >
                        <span
                          class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                          :class="
                            i === ticket.ticket_type_path.length - 1
                              ? 'bg-n-brand/10 text-n-brand border border-n-brand/20'
                              : 'text-n-slate-9'
                          "
                          >{{ seg }}</span>
                        <span
                          v-if="i < ticket.ticket_type_path.length - 1"
                          class="i-lucide-chevron-right size-2.5 text-n-slate-7 flex-shrink-0"
                        />
                      </template>
                    </div>
                    <!-- Checklist summary -->
                    <div
                      v-if="ticket.checklist?.length"
                      class="flex items-center gap-2 mt-1.5 mb-0.5"
                    >
                      <div
                        class="flex-1 h-1 bg-n-alpha-3 rounded-full overflow-hidden max-w-[56px]"
                      >
                        <div
                          class="h-full rounded-full transition-all duration-300"
                          :class="
                            ticketChecklistProgress(ticket) === 100
                              ? 'bg-emerald-500'
                              : 'bg-n-brand/60'
                          "
                          :style="`width: ${ticketChecklistProgress(ticket)}%`"
                        />
                      </div>
                      <span class="text-[10px] text-n-slate-9 flex-shrink-0">
                        {{ ticketChecklistDone(ticket) }}/{{
                          ticket.checklist.length
                        }}
                      </span>
                      <span
                        v-if="ticketEstimatedMinutes(ticket)"
                        class="flex items-center gap-0.5 text-[10px] text-n-slate-8 flex-shrink-0 ml-auto"
                      >
                        <span class="i-lucide-clock-3 size-2.5" />
                        {{ formatMinutes(ticketEstimatedMinutes(ticket)) }}
                      </span>
                    </div>

                    <!-- Footer -->
                    <div class="flex items-center justify-between mt-2">
                      <div class="flex items-center gap-1.5">
                        <div
                          v-for="agentId in ticket.assignee_ids?.slice(0, 4)"
                          :key="agentId"
                          class="size-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                          :class="avatarColor(agentId)"
                          :title="getAgent(agentId)?.name"
                        >
                          {{ agentInitials(getAgent(agentId)) }}
                        </div>
                        <span
                          v-if="(ticket.assignee_ids?.length || 0) > 4"
                          class="text-[10px] text-n-slate-9"
                          >+{{ ticket.assignee_ids.length - 4 }}</span>
                        <span
                          v-if="!ticket.assignee_ids?.length"
                          class="text-[11px] text-n-slate-8 italic"
                          >Sem responsável</span>
                      </div>
                      <div class="flex items-center gap-3" @click.stop>
                        <div
                          v-if="ticket.due_date"
                          class="flex items-center gap-1"
                        >
                          <span
                            class="size-3.5"
                            :class="dueDateInfo(ticket.due_date).icon"
                          />
                          <span
                            class="text-[11px]"
                            :class="dueDateInfo(ticket.due_date).classes"
                            >{{ dueDateInfo(ticket.due_date).label }}</span>
                        </div>
                        <div class="hidden group-hover:flex items-center gap-1">
                          <button
                            v-if="ticket.status !== 'in_progress'"
                            class="px-2 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60 transition-colors"
                            @click="quickStatusChange(ticket, 'in_progress')"
                          >
                            Iniciar
                          </button>
                          <button
                            v-if="ticket.status !== 'open'"
                            class="px-2 py-0.5 rounded text-[10px] bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-900/40 dark:text-rose-300 transition-colors"
                            @click="quickStatusChange(ticket, 'open')"
                          >
                            Reabrir
                          </button>
                          <button
                            class="px-2 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 transition-colors"
                            @click="quickStatusChange(ticket, 'done')"
                          >
                            Concluir
                          </button>
                          <button
                            class="p-0.5 rounded text-n-slate-9 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                            @click="deleteConfirmId = ticket.id"
                          >
                            <span class="i-lucide-trash-2 size-3.5" />
                          </button>
                        </div>
                        <span class="text-[10px] text-n-slate-8">{{
                          formatDate(ticket.created_at)
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Draggable>

            <!-- ─── List mode ───────────────────────────────────────────────── -->
            <Draggable
              v-else
              :model-value="activeTickets"
              item-key="id"
              class="flex flex-col gap-1"
              handle=".drag-handle"
              animation="200"
              ghost-class="opacity-40"
              @change="handleActiveReorder"
            >
              <template #item="{ element: ticket }">
                <div
                  class="group relative flex items-center gap-3 bg-n-alpha-1 border border-n-weak rounded-lg border-l-4 hover:shadow-sm transition-all duration-150 cursor-pointer py-2 px-3"
                  :class="PRIORITY_CONFIG[ticket.priority]?.border"
                  @click="openEditModal(ticket)"
                >
                  <!-- Drag handle -->
                  <span
                    class="drag-handle i-lucide-grip-vertical size-4 text-n-slate-7 hover:text-n-slate-10 cursor-grab flex-shrink-0"
                    title="Arrastar para reordenar"
                    @click.stop
                  />
                  <!-- Contact -->
                  <div class="w-32 flex-shrink-0 min-w-0">
                    <p class="text-xs font-medium text-n-slate-12 truncate">
                      {{ ticket.contact_name || '—' }}
                    </p>
                    <p
                      v-if="ticket.company_name"
                      class="text-[10px] text-n-slate-9 truncate"
                    >
                      {{ ticket.company_name }}
                    </p>
                  </div>
                  <!-- Title + type path -->
                  <div class="flex-1 min-w-0">
                    <p
                      class="text-xs font-bold text-n-slate-12 uppercase tracking-wide truncate"
                    >
                      {{ ticket.title }}
                    </p>
                    <div
                      v-if="ticket.ticket_type_path?.length"
                      class="flex items-center gap-0.5 mt-0.5"
                    >
                      <template
                        v-for="(seg, i) in ticket.ticket_type_path"
                        :key="i"
                      >
                        <span
                          class="text-[10px]"
                          :class="
                            i === ticket.ticket_type_path.length - 1
                              ? 'text-n-brand font-medium'
                              : 'text-n-slate-9'
                          "
                          >{{ seg }}</span>
                        <span
                          v-if="i < ticket.ticket_type_path.length - 1"
                          class="i-lucide-chevron-right size-2 text-n-slate-7"
                        />
                      </template>
                    </div>
                  </div>
                  <!-- Badges -->
                  <div class="flex items-center gap-1 flex-shrink-0">
                    <span
                      class="px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                      :class="STATUS_CONFIG[ticket.status]?.badge"
                      >{{ STATUS_CONFIG[ticket.status]?.label }}</span>
                    <span
                      class="px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                      :class="PRIORITY_CONFIG[ticket.priority]?.badge"
                      >{{ PRIORITY_CONFIG[ticket.priority]?.label }}</span>
                  </div>
                  <!-- Assignees -->
                  <div class="flex items-center flex-shrink-0">
                    <div
                      v-for="agentId in ticket.assignee_ids?.slice(0, 3)"
                      :key="agentId"
                      class="size-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ring-1 ring-n-background -ml-1 first:ml-0"
                      :class="avatarColor(agentId)"
                      :title="getAgent(agentId)?.name"
                    >
                      {{ agentInitials(getAgent(agentId)) }}
                    </div>
                    <span
                      v-if="!ticket.assignee_ids?.length"
                      class="text-[10px] text-n-slate-8 italic ml-1"
                      >—</span>
                  </div>
                  <!-- Checklist + estimated time -->
                  <div class="flex items-center gap-1.5 flex-shrink-0 w-28">
                    <template v-if="ticket.checklist?.length">
                      <div
                        class="w-10 h-1 bg-n-alpha-3 rounded-full overflow-hidden flex-shrink-0"
                      >
                        <div
                          class="h-full rounded-full transition-all duration-300"
                          :class="
                            ticketChecklistProgress(ticket) === 100
                              ? 'bg-emerald-500'
                              : 'bg-n-brand/60'
                          "
                          :style="`width: ${ticketChecklistProgress(ticket)}%`"
                        />
                      </div>
                      <span class="text-[10px] text-n-slate-9 flex-shrink-0">{{ ticketChecklistDone(ticket) }}/{{
                          ticket.checklist.length
                        }}</span>
                      <span
                        v-if="ticketEstimatedMinutes(ticket)"
                        class="flex items-center gap-0.5 text-[10px] text-n-slate-8 flex-shrink-0"
                      >
                        <span class="i-lucide-clock-3 size-2.5" />
                        {{ formatMinutes(ticketEstimatedMinutes(ticket)) }}
                      </span>
                    </template>
                  </div>
                  <!-- Due date -->
                  <div class="w-20 flex-shrink-0 text-right">
                    <span
                      v-if="ticket.due_date"
                      class="text-[10px]"
                      :class="dueDateInfo(ticket.due_date).classes"
                      >{{ dueDateInfo(ticket.due_date).label }}</span>
                    <span v-else
class="text-[10px] text-n-slate-8"
                      >Sem prazo</span>
                  </div>
                  <!-- Quick actions -->
                  <div
                    class="hidden group-hover:flex items-center gap-1 flex-shrink-0"
                    @click.stop
                  >
                    <button
                      v-if="ticket.status !== 'in_progress'"
                      class="px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 transition-colors"
                      @click="quickStatusChange(ticket, 'in_progress')"
                    >
                      Iniciar
                    </button>
                    <button
                      v-if="ticket.status !== 'open'"
                      class="px-1.5 py-0.5 rounded text-[10px] bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-900/40 dark:text-rose-300 transition-colors"
                      @click="quickStatusChange(ticket, 'open')"
                    >
                      Reabrir
                    </button>
                    <button
                      class="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 transition-colors"
                      @click="quickStatusChange(ticket, 'done')"
                    >
                      Concluir
                    </button>
                    <button
                      class="p-0.5 rounded text-n-slate-9 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      @click="deleteConfirmId = ticket.id"
                    >
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </div>
              </template>
            </Draggable>
          </template>
        </div>

        <!-- ── Done tickets ────────────────────────────────────────────── -->
        <div v-if="doneTickets.length > 0" class="border-t border-n-weak pt-4">
          <button
            class="flex items-center gap-2 text-sm font-medium text-n-slate-10 hover:text-n-slate-12 transition-colors mb-3"
            @click="showDone = !showDone"
          >
            <span
              class="i-lucide-chevron-right size-4 transition-transform"
              :class="{ 'rotate-90': showDone }"
            />
            Concluídos ({{ doneTickets.length }})
          </button>

          <Draggable
            v-if="showDone"
            :model-value="doneTickets"
            item-key="id"
            class="grid gap-2"
            handle=".drag-handle"
            animation="200"
            ghost-class="opacity-40"
            @change="handleDoneReorder"
          >
            <template #item="{ element: ticket }">
              <div
                class="group relative bg-n-alpha-1 border border-n-weak rounded-xl border-l-4 border-l-emerald-400 opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                @click="openEditModal(ticket)"
              >
                <div class="p-3 flex items-center gap-3">
                  <span
                    class="drag-handle i-lucide-grip-vertical size-4 text-n-slate-6 hover:text-n-slate-9 cursor-grab flex-shrink-0"
                    title="Arrastar para reordenar"
                    @click.stop
                  />
                  <span
                    class="i-lucide-circle-check-big size-4 text-emerald-500 flex-shrink-0"
                  />
                  <div class="min-w-0 flex-1">
                    <p
                      class="text-sm font-semibold text-n-slate-11 truncate line-through"
                    >
                      {{ ticket.title }}
                    </p>
                    <p
                      v-if="ticket.contact_name"
                      class="text-xs text-n-slate-9 truncate"
                    >
                      {{ ticket.contact_name }}
                    </p>
                  </div>
                  <div
                    class="flex items-center gap-2 flex-shrink-0"
                    @click.stop
                  >
                    <button
                      class="hidden group-hover:inline-flex px-2 py-0.5 rounded text-[10px] bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-900/40 dark:text-rose-300 transition-colors"
                      @click="quickStatusChange(ticket, 'open')"
                    >
                      Reabrir
                    </button>
                    <span class="text-[10px] text-n-slate-8">{{
                      formatDate(ticket.updated_at)
                    }}</span>
                  </div>
                </div>
              </div>
            </template>
          </Draggable>
        </div>
      </template>
    </div>

    <!-- ── Delete Confirm Overlay ─────────────────────────────────────────── -->
    <div
      v-if="deleteConfirmId"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="deleteConfirmId = null"
    >
      <div
        class="bg-n-background border border-n-weak rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4"
      >
        <div class="flex items-center gap-3 mb-4">
          <span class="i-lucide-triangle-alert size-5 text-red-500" />
          <h3 class="text-base font-semibold text-n-slate-12">
            Excluir ticket?
          </h3>
        </div>
        <p class="text-sm text-n-slate-10 mb-5">
          Esta ação não pode ser desfeita.
        </p>
        <div class="flex gap-2 justify-end">
          <button
            class="px-4 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="deleteConfirmId = null"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition-colors"
            @click="deleteTicket(deleteConfirmId)"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal ──────────────────────────────────────────────────────────── -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="closeModal"
    >
      <div
        class="bg-n-background border border-n-weak rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col"
      >
        <!-- Modal header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
        >
          <div class="flex items-center gap-2 min-w-0">
            <span class="i-lucide-ticket size-5 text-n-brand flex-shrink-0" />
            <h2 class="text-base font-semibold text-n-slate-12 flex-shrink-0">
              {{ editingTicket ? 'Editar Ticket' : 'Novo Ticket' }}
            </h2>
            <Transition
              enter-active-class="transition-all duration-300"
              enter-from-class="opacity-0 scale-90"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition-all duration-200"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-90"
            >
              <span
                v-if="totalEstimatedMinutes"
                class="flex items-center gap-1 text-[10px] font-medium text-n-slate-9 bg-n-alpha-2 px-2 py-0.5 rounded-full border border-n-weak flex-shrink-0"
              >
                <span class="i-lucide-clock size-2.5" />
                {{ formatMinutes(totalEstimatedMinutes) }} restantes
              </span>
            </Transition>
          </div>
          <button
            class="size-8 flex items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            @click="closeModal"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Modal body -->
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <!-- Contact + Company -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Nome do contato</label>
              <input
                v-model="form.contact_name"
                type="text"
                placeholder="Nome da pessoa"
                class="w-full px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-8"
              />
            </div>
            <!-- Company picker -->
            <div class="relative" data-company-picker>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Empresa</label>
              <button
                type="button"
                class="w-full flex items-center gap-2 px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-lg outline-none hover:border-n-brand transition-colors text-left"
                :class="
                  form.company_name ? 'text-n-slate-12' : 'text-n-slate-8'
                "
                @click.stop="
                  showCompanyPicker = !showCompanyPicker;
                  companySearch = '';
                "
              >
                <span
                  class="i-lucide-building-2 size-3.5 flex-shrink-0 text-n-slate-9"
                />
                <span class="flex-1 truncate">{{
                  form.company_name || 'Selecionar empresa...'
                }}</span>
                <span
                  v-if="form.company_name"
                  class="i-lucide-x size-3.5 text-n-slate-8 hover:text-red-500 flex-shrink-0"
                  @click.stop="
                    form.company_name = '';
                    form.contact_name = '';
                  "
                />
                <span
                  v-else
                  class="i-lucide-chevron-down size-3.5 text-n-slate-8 flex-shrink-0"
                />
              </button>

              <!-- Dropdown picker -->
              <div
                v-if="showCompanyPicker"
                class="absolute top-full left-0 right-0 mt-1 z-50 bg-n-background border border-n-weak rounded-xl shadow-2xl overflow-hidden"
              >
                <!-- Search input -->
                <div class="p-2 border-b border-n-weak">
                  <div class="relative">
                    <span
                      class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-9"
                    />
                    <input
                      v-model="companySearch"
                      type="text"
                      placeholder="Buscar por empresa ou responsável..."
                      class="w-full pl-8 pr-3 py-1.5 text-xs bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:border-n-brand text-n-slate-12 placeholder:text-n-slate-8"
                      @click.stop
                    />
                  </div>
                </div>
                <!-- List -->
                <div class="max-h-52 overflow-y-auto">
                  <template v-if="filteredCompanies.length">
                    <button
                      v-for="company in filteredCompanies"
                      :key="company.id"
                      type="button"
                      class="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-n-alpha-2 transition-colors text-left"
                      :class="
                        form.company_name === company.name ? 'bg-n-brand/5' : ''
                      "
                      @click.stop="selectCompany(company)"
                    >
                      <div
                        class="size-7 rounded-lg bg-n-alpha-2 flex items-center justify-center flex-shrink-0"
                      >
                        <span
                          class="i-lucide-building-2 size-3.5 text-n-slate-9"
                        />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p
                          class="text-xs font-semibold text-n-slate-12 truncate"
                        >
                          {{ company.name }}
                        </p>
                        <p
                          v-if="company.responsible_name"
                          class="text-[10px] text-n-slate-9 truncate"
                        >
                          <span
                            class="i-lucide-user size-2.5 mr-0.5 inline-block align-middle"
                          />
                          {{ company.responsible_name }}
                        </p>
                      </div>
                      <span
                        v-if="form.company_name === company.name"
                        class="i-lucide-check size-3.5 text-n-brand flex-shrink-0"
                      />
                    </button>
                  </template>
                  <div
                    v-else
                    class="flex flex-col items-center gap-1.5 py-6 text-center"
                  >
                    <span class="i-lucide-building-2 size-7 text-n-slate-7" />
                    <p class="text-xs text-n-slate-9">
                      {{
                        companySearch
                          ? 'Nenhuma empresa encontrada'
                          : 'Nenhuma empresa cadastrada'
                      }}
                    </p>
                    <button
                      v-if="!companySearch"
                      type="button"
                      class="text-xs text-n-brand underline"
                      @click.stop="
                        showCompanyManager = true;
                        showCompanyPicker = false;
                      "
                    >
                      Cadastrar empresa
                    </button>
                  </div>
                </div>
                <!-- Footer: tipo manualmente -->
                <div
                  class="border-t border-n-weak px-3 py-2 flex items-center justify-between"
                >
                  <span class="text-[10px] text-n-slate-8">ou digite manualmente abaixo</span>
                  <button
                    type="button"
                    class="text-[10px] text-n-brand hover:underline"
                    @click.stop="
                      showCompanyManager = true;
                      showCompanyPicker = false;
                    "
                  >
                    + Cadastrar empresa
                  </button>
                </div>
              </div>

              <!-- Manual override input (shown below picker) -->
              <input
                v-if="!showCompanyPicker"
                v-model="form.company_name"
                type="text"
                placeholder="ou digitar manualmente"
                class="mt-1 w-full px-3 py-1.5 text-xs bg-n-alpha-1 border border-dashed border-n-weak rounded-lg outline-none focus:border-n-brand text-n-slate-12 placeholder:text-n-slate-8"
              />
            </div>
          </div>

          <!-- Title -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Título <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.title"
              type="text"
              placeholder="Ex: AGENDAMENTOS COM FALHAS"
              class="w-full px-3 py-2 text-sm font-semibold uppercase tracking-wide bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-8 placeholder:font-normal placeholder:normal-case placeholder:tracking-normal"
            />
          </div>

          <!-- Subtitle -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Subtítulo
              <span class="text-n-slate-8 font-normal">(visível apenas ao abrir)</span>
            </label>
            <input
              v-model="form.subtitle"
              type="text"
              placeholder="Ex: 📍 01/04/2026 - 21h22"
              class="w-full px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-8"
            />
          </div>

          <!-- Description -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="text-xs font-medium text-n-slate-11">
                Descrição
                <span class="text-n-slate-8 font-normal">(visível apenas ao abrir)</span>
              </label>
              <button
                type="button"
                class="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                title="Abrir editor em tela cheia"
                @click="openDescriptionEditor"
              >
                <span class="i-lucide-maximize-2 size-3" />
                Expandir
              </button>
            </div>
            <textarea
              ref="descriptionRef"
              v-model="form.description"
              placeholder="Descreva a demanda em detalhes..."
              class="w-full px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-8 resize-none overflow-y-hidden min-h-[80px] transition-[height] duration-150"
              @input="autoResizeDescription"
            />
          </div>

          <!-- Checklist -->
          <div>
            <!-- Header -->
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <label class="text-xs font-medium text-n-slate-11">Checklist</label>
                <template v-if="form.checklist.length">
                  <span
                    class="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full transition-all duration-300"
                    :class="
                      checklistProgress === 100
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-n-alpha-2 text-n-slate-10'
                    "
                  >
                    <span
                      v-if="checklistProgress === 100"
                      class="i-lucide-party-popper size-2.5"
                    />
                    {{
                      checklistProgress === 100
                        ? 'Tudo concluído!'
                        : `${doneChecklistCount}/${form.checklist.length}`
                    }}
                  </span>
                </template>
              </div>
              <button
                type="button"
                class="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-brand transition-colors"
                @click="addChecklistItem"
              >
                <span class="i-lucide-plus size-3" />
                Adicionar item
              </button>
            </div>

            <!-- Progress bar -->
            <div
              v-if="form.checklist.length"
              class="h-1 bg-n-alpha-2 rounded-full mb-3 overflow-hidden"
            >
              <div
                class="h-full rounded-full transition-all duration-500"
                :class="
                  checklistProgress === 100 ? 'bg-emerald-500' : 'bg-n-brand'
                "
                :style="`width: ${checklistProgress}%`"
              />
            </div>

            <!-- Items (draggable) -->
            <Draggable
              v-if="form.checklist.length"
              v-model="form.checklist"
              item-key="id"
              handle=".checklist-drag-handle"
              animation="150"
              ghost-class="opacity-40"
              class="space-y-0.5"
            >
              <template #item="{ element: item, index: idx }">
                <div
                  class="group flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-n-alpha-1 transition-colors"
                >
                  <!-- Drag handle -->
                  <span
                    class="checklist-drag-handle flex-shrink-0 i-lucide-grip-vertical size-3.5 text-n-slate-6 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
                  />

                  <!-- Checkbox -->
                  <button
                    type="button"
                    class="flex-shrink-0 size-[18px] rounded border-2 transition-all duration-200 flex items-center justify-center"
                    :class="
                      item.done
                        ? 'bg-emerald-500 border-emerald-500 shadow-sm'
                        : 'border-n-slate-6 hover:border-emerald-400 bg-transparent'
                    "
                    @click="item.done = !item.done"
                  >
                    <span
                      v-if="item.done"
                      class="i-lucide-check size-2.5 text-white"
                    />
                  </button>

                  <!-- Text -->
                  <input
                    :ref="
                      el => {
                        checklistRefs[idx] = el;
                      }
                    "
                    v-model="item.text"
                    type="text"
                    placeholder="Item do checklist..."
                    class="flex-1 min-w-0 text-sm bg-transparent outline-none placeholder:text-n-slate-7 transition-all"
                    :class="
                      item.done
                        ? 'line-through text-n-slate-8'
                        : 'text-n-slate-12'
                    "
                    @keydown.enter.prevent="addChecklistItemAfter(idx)"
                    @keydown.backspace="handleChecklistBackspace($event, idx)"
                  />

                  <!-- Estimated time -->
                  <div
                    class="flex items-center gap-0.5 flex-shrink-0 focus-within:opacity-100 transition-opacity"
                    :class="
                      item.estimated_minutes
                        ? 'opacity-100'
                        : 'opacity-0 group-hover:opacity-100'
                    "
                  >
                    <span
                      class="i-lucide-clock-3 size-3 text-n-slate-7 flex-shrink-0"
                    />
                    <input
                      type="number"
                      min="0"
                      max="999"
                      :value="Math.floor((item.estimated_minutes || 0) / 60)"
                      placeholder="0"
                      class="w-7 text-center text-[10px] bg-transparent outline-none text-n-slate-10 border-b border-transparent focus:border-n-brand [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      @input="
                        e => updateEstimatedTime(item, 'hours', e.target.value)
                      "
                    />
                    <span class="text-[10px] text-n-slate-7 leading-none">h</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      :value="(item.estimated_minutes || 0) % 60"
                      placeholder="0"
                      class="w-7 text-center text-[10px] bg-transparent outline-none text-n-slate-10 border-b border-transparent focus:border-n-brand [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      @input="
                        e => updateEstimatedTime(item, 'mins', e.target.value)
                      "
                    />
                    <span class="text-[10px] text-n-slate-7 leading-none">m</span>
                  </div>

                  <!-- Delete -->
                  <button
                    type="button"
                    class="flex-shrink-0 size-5 flex items-center justify-center rounded text-n-slate-7 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
                    @click="removeChecklistItem(idx)"
                  >
                    <span class="i-lucide-x size-3" />
                  </button>
                </div>
              </template>
            </Draggable>

            <!-- Empty state -->
            <button
              v-else
              type="button"
              class="w-full flex items-center gap-2 py-3 px-3 bg-n-alpha-1 border border-dashed border-n-weak rounded-lg hover:border-n-brand hover:bg-n-brand/5 transition-colors group"
              @click="addChecklistItem"
            >
              <span
                class="i-lucide-check-square size-4 text-n-slate-8 group-hover:text-n-brand transition-colors"
              />
              <span
                class="text-xs text-n-slate-8 group-hover:text-n-brand transition-colors"
                >Adicionar checklist à demanda...</span>
            </button>
          </div>

          <!-- Priority + Status + Due date -->
          <div class="grid grid-cols-3 gap-4">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Prioridade <span class="text-red-500">*</span></label>
              <div class="grid grid-cols-2 gap-1.5">
                <button
                  v-for="p in ['urgent', 'high', 'medium', 'low']"
                  :key="p"
                  class="px-2 py-1.5 rounded-lg text-xs font-semibold transition-all border"
                  :class="
                    form.priority === p
                      ? PRIORITY_CONFIG[p].color +
                        ' border-transparent shadow-sm'
                      : 'border-n-weak text-n-slate-10 hover:bg-n-alpha-2'
                  "
                  @click="form.priority = p"
                >
                  {{ PRIORITY_CONFIG[p].label }}
                </button>
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Status <span class="text-red-500">*</span></label>
              <div class="flex flex-col gap-1.5">
                <button
                  v-for="s in ['open', 'in_progress', 'done']"
                  :key="s"
                  class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-all border"
                  :class="
                    form.status === s
                      ? STATUS_CONFIG[s].badge + ' border-transparent'
                      : 'border-n-weak text-n-slate-10 hover:bg-n-alpha-2'
                  "
                  @click="form.status = s"
                >
                  <span
                    class="size-2 rounded-full"
                    :class="STATUS_CONFIG[s].dot"
                  />
                  {{ STATUS_CONFIG[s].label }}
                </button>
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Prazo de entrega</label>
              <input
                v-model="form.due_date"
                type="date"
                class="w-full px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12"
              />
            </div>
          </div>

          <!-- Tipo hierárquico (seletor cascata) -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="text-xs font-medium text-n-slate-11">Tipo do Ticket</label>
              <!-- Breadcrumb do selecionado -->
              <div v-if="selectedTypeId" class="flex items-center gap-1">
                <template
                  v-for="(seg, i) in typePathLabel(selectedTypeId)"
                  :key="i"
                >
                  <span
                    class="text-[10px]"
                    :class="
                      i === typePathLabel(selectedTypeId).length - 1
                        ? 'text-n-brand font-semibold'
                        : 'text-n-slate-9'
                    "
                    >{{ seg }}</span>
                  <span
                    v-if="i < typePathLabel(selectedTypeId).length - 1"
                    class="i-lucide-chevron-right size-2.5 text-n-slate-7"
                  />
                </template>
                <button
                  class="ml-1 size-3.5 flex items-center justify-center rounded text-n-slate-8 hover:text-red-500"
                  @click="cascadePath = []"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </div>
              <span v-else
class="text-[10px] text-n-slate-8 italic"
                >Nenhum selecionado</span>
            </div>

            <!-- Sem tipos cadastrados -->
            <div
              v-if="!ticketTypes.length"
              class="flex items-center gap-2 py-3 px-3 bg-n-alpha-1 border border-dashed border-n-weak rounded-lg text-n-slate-9 text-xs"
            >
              <span class="i-lucide-git-branch size-4" />
              <span>Nenhum tipo cadastrado.
                <button
                  class="text-n-brand underline"
                  @click="
                    showTypeManager = true;
                    showModal = false;
                  "
                >
                  Criar tipos
                </button></span>
            </div>

            <!-- Colunas cascata -->
            <div v-else class="flex gap-2 overflow-x-auto pb-1">
              <div
                v-for="(col, colIdx) in cascadeColumns"
                :key="colIdx"
                class="flex flex-col gap-1 min-w-[130px] flex-shrink-0"
              >
                <!-- Cabeçalho da coluna -->
                <p
                  class="text-[9px] font-semibold uppercase tracking-wider text-n-slate-8 px-1 mb-0.5"
                >
                  {{
                    colIdx === 0
                      ? 'Categoria'
                      : typesMap[cascadePath[colIdx - 1]]?.name
                  }}
                </p>
                <button
                  v-for="node in col"
                  :key="node.id"
                  class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border text-left"
                  :class="
                    cascadePath[colIdx] === node.id
                      ? 'bg-n-brand text-white border-n-brand shadow-sm'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2 bg-n-alpha-1'
                  "
                  @click="cascadeSelect(colIdx, node.id)"
                >
                  <span
                    class="size-2 rounded-full flex-shrink-0"
                    :style="node.color ? `background:${node.color}` : ''"
                    :class="!node.color ? 'bg-n-slate-7' : ''"
                  />
                  <span class="flex-1 truncate">{{ node.name }}</span>
                  <span
                    v-if="node.children?.length"
                    class="i-lucide-chevron-right size-3 opacity-60 flex-shrink-0"
                  />
                </button>
              </div>
            </div>
          </div>

          <!-- Assignees -->
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-2">Responsáveis</label>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="agent in agents"
                :key="agent.id"
                class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border"
                :class="
                  form.assignee_ids.includes(agent.id)
                    ? 'bg-n-brand/10 border-n-brand text-n-brand dark:bg-n-brand/20'
                    : 'border-n-weak text-n-slate-10 hover:bg-n-alpha-2'
                "
                @click="toggleAssignee(agent.id)"
              >
                <div
                  class="size-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white"
                  :class="avatarColor(agent.id)"
                >
                  {{ agentInitials(agent) }}
                </div>
                {{ agentFirstName(agent) }}
              </button>
            </div>
            <p v-if="!agents.length" class="text-xs text-n-slate-8 mt-1">
              Nenhum agente disponível
            </p>
          </div>

          <!-- Show subtitle/description if editing and they exist -->
          <div
            v-if="
              editingTicket &&
              (editingTicket.subtitle || editingTicket.description)
            "
            class="rounded-xl bg-n-alpha-2 p-4 border border-n-weak"
          >
            <p
              v-if="editingTicket.subtitle"
              class="text-sm font-medium text-n-slate-11 mb-2"
            >
              📍 {{ editingTicket.subtitle }}
            </p>
            <p
              v-if="editingTicket.description"
              class="text-sm text-n-slate-10 whitespace-pre-wrap"
            >
              {{ editingTicket.description }}
            </p>
            <p class="text-xs text-n-slate-8 mt-2">
              Criado em {{ formatDateTime(editingTicket.created_at) }}
              <template v-if="editingTicket.created_by">
                · por {{ editingTicket.created_by.name }}
              </template>
            </p>
          </div>
        </div>

        <!-- Modal footer -->
        <div
          class="flex items-center justify-between px-6 py-4 border-t border-n-weak flex-shrink-0"
        >
          <button
            v-if="editingTicket"
            class="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 transition-colors"
            @click="
              deleteConfirmId = editingTicket.id;
              closeModal();
            "
          >
            <span class="i-lucide-trash-2 size-4" />
            Excluir
          </button>
          <div v-else />
          <div class="flex gap-2">
            <button
              class="px-4 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button
              class="flex items-center gap-2 px-5 py-2 rounded-lg text-sm bg-n-brand text-white hover:bg-n-brand/90 transition-colors disabled:opacity-50"
              :disabled="!form.title.trim() || isSaving"
              @click="saveTicket"
            >
              <span
                v-if="isSaving"
                class="i-lucide-loader-circle size-4 animate-spin"
              />
              {{ editingTicket ? 'Salvar alterações' : 'Criar ticket' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Description Fullscreen Editor ───────────────────────────────────── -->
    <div
      v-if="descriptionExpanded"
      class="fixed inset-0 z-[60] flex flex-col bg-n-background"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
      >
        <div class="flex items-center gap-3">
          <button
            class="size-8 flex items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            title="Voltar ao formulário (Esc)"
            @click="descriptionExpanded = false"
          >
            <span class="i-lucide-arrow-left size-4" />
          </button>
          <div>
            <h2 class="text-sm font-semibold text-n-slate-12">Descrição</h2>
            <p class="text-xs text-n-slate-9 truncate max-w-xs">
              {{ form.title || 'Novo ticket' }}
            </p>
          </div>
        </div>
        <button
          class="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-n-brand text-white hover:bg-n-brand/90 transition-colors"
          @click="descriptionExpanded = false"
        >
          <span class="i-lucide-check size-4" />
          Concluído
        </button>
      </div>

      <!-- Editor area -->
      <div class="flex-1 relative">
        <textarea
          ref="descriptionExpandedRef"
          v-model="form.description"
          placeholder="Descreva a demanda em detalhes..."
          class="absolute inset-0 w-full h-full px-10 py-8 text-sm bg-n-background outline-none text-n-slate-12 placeholder:text-n-slate-8 resize-none leading-relaxed"
          @keydown.esc="descriptionExpanded = false"
        />
      </div>

      <!-- Footer status bar -->
      <div
        class="flex items-center justify-between px-6 py-2.5 border-t border-n-weak flex-shrink-0 bg-n-alpha-1"
      >
        <div class="flex items-center gap-4 text-xs text-n-slate-8">
          <span>{{ form.description.length }} caracteres</span>
          <span v-if="form.description.trim()">
            {{ form.description.trim().split(/\s+/).filter(Boolean).length }}
            palavras
          </span>
        </div>
        <span class="text-xs text-n-slate-7">Pressione Esc ou clique em Concluído para fechar</span>
      </div>
    </div>

    <!-- ── Company Manager Modal ─────────────────────────────────────────── -->
    <div
      v-if="showCompanyManager"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="
        showCompanyManager = false;
        closeCompanyForm();
      "
    >
      <div
        class="bg-n-background border border-n-weak rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-building-2 size-5 text-n-brand" />
            <h2 class="text-base font-semibold text-n-slate-12">
              Cadastro de Empresas
            </h2>
          </div>
          <button
            class="size-8 flex items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 transition-colors"
            @click="
              showCompanyManager = false;
              closeCompanyForm();
            "
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-6 space-y-4">
          <!-- Inline form -->
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs font-semibold text-n-slate-11 mb-3">
              {{
                editingCompany
                  ? `Editando: ${editingCompany.name}`
                  : 'Nova empresa'
              }}
            </p>
            <div class="flex flex-col gap-2">
              <div>
                <label
                  class="block text-[10px] font-medium text-n-slate-10 mb-1"
                  >Nome da empresa <span class="text-red-500">*</span></label>
                <input
                  v-model="companyForm.name"
                  type="text"
                  placeholder="Ex: Acme Ltda, OAB Prev..."
                  class="w-full px-3 py-2 text-sm bg-n-background border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-8"
                  @keydown.enter="saveCompany"
                />
              </div>
              <div>
                <label
                  class="block text-[10px] font-medium text-n-slate-10 mb-1"
                  >Responsável</label>
                <input
                  v-model="companyForm.responsible_name"
                  type="text"
                  placeholder="Nome do responsável"
                  class="w-full px-3 py-2 text-sm bg-n-background border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-8"
                  @keydown.enter="saveCompany"
                />
              </div>
              <div class="flex gap-2 mt-1">
                <button
                  class="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-n-brand text-white hover:bg-n-brand/90 transition-colors disabled:opacity-50"
                  :disabled="!companyForm.name.trim() || isSavingCompany"
                  @click="saveCompany"
                >
                  <span
                    v-if="isSavingCompany"
                    class="i-lucide-loader-circle size-4 animate-spin"
                  />
                  <span v-else class="i-lucide-check size-4" />
                  {{ editingCompany ? 'Salvar' : 'Cadastrar' }}
                </button>
                <button
                  v-if="editingCompany"
                  class="px-4 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                  @click="closeCompanyForm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>

          <!-- Company list -->
          <div
            v-if="!ticketCompanies.length"
            class="flex flex-col items-center gap-2 py-10 text-n-slate-9"
          >
            <span class="i-lucide-building-2 size-10" />
            <p class="text-sm">Nenhuma empresa cadastrada ainda.</p>
          </div>

          <div v-else class="space-y-1">
            <div
              v-for="company in ticketCompanies"
              :key="company.id"
              class="flex items-center gap-3 px-3 py-2.5 bg-n-alpha-1 border border-n-weak rounded-xl"
            >
              <div
                class="size-8 rounded-lg bg-n-alpha-2 flex items-center justify-center flex-shrink-0"
              >
                <span class="i-lucide-building-2 size-4 text-n-slate-9" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-n-slate-12 truncate">
                  {{ company.name }}
                </p>
                <p
                  v-if="company.responsible_name"
                  class="text-xs text-n-slate-9 truncate"
                >
                  <span
                    class="i-lucide-user size-3 mr-0.5 inline-block align-middle"
                  />
                  {{ company.responsible_name }}
                </p>
                <p v-else class="text-xs text-n-slate-7 italic">
                  Sem responsável
                </p>
              </div>
              <div class="flex items-center gap-1 flex-shrink-0">
                <button
                  class="size-7 flex items-center justify-center rounded-lg text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                  title="Editar"
                  @click="openCompanyForm(company)"
                >
                  <span class="i-lucide-pencil size-3.5" />
                </button>
                <button
                  class="size-7 flex items-center justify-center rounded-lg text-n-slate-9 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 transition-colors"
                  title="Excluir"
                  @click="companyDeleteConfirmId = company.id"
                >
                  <span class="i-lucide-trash-2 size-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirm empresa -->
    <div
      v-if="companyDeleteConfirmId"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      @click.self="companyDeleteConfirmId = null"
    >
      <div
        class="bg-n-background border border-n-weak rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4"
      >
        <div class="flex items-center gap-3 mb-4">
          <span class="i-lucide-triangle-alert size-5 text-red-500" />
          <h3 class="text-base font-semibold text-n-slate-12">
            Excluir empresa?
          </h3>
        </div>
        <p class="text-sm text-n-slate-10 mb-5">
          Esta ação não pode ser desfeita.
        </p>
        <div class="flex gap-2 justify-end">
          <button
            class="px-4 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="companyDeleteConfirmId = null"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition-colors"
            @click="deleteCompany(companyDeleteConfirmId)"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>

    <!-- ── Type Manager Modal ─────────────────────────────────────────────── -->
    <div
      v-if="showTypeManager"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      @click.self="
        showTypeManager = false;
        closeTypeForm();
      "
    >
      <div
        class="bg-n-background border border-n-weak rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-git-branch size-5 text-n-brand" />
            <h2 class="text-base font-semibold text-n-slate-12">
              Gerenciar Tipos de Ticket
            </h2>
          </div>
          <button
            class="size-8 flex items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 transition-colors"
            @click="
              showTypeManager = false;
              closeTypeForm();
            "
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-6 space-y-4">
          <!-- Inline form para criar/editar -->
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs font-semibold text-n-slate-11 mb-3">
              {{
                editingType
                  ? `Editando: ${editingType.name}`
                  : typeForm.parent_id
                    ? `Adicionar filho de "${typesMap[typeForm.parent_id]?.name}"`
                    : 'Novo tipo raiz'
              }}
            </p>
            <div class="flex gap-2 items-end">
              <div class="flex-1">
                <label
                  class="block text-[10px] font-medium text-n-slate-10 mb-1"
                  >Nome</label>
                <input
                  v-model="typeForm.name"
                  type="text"
                  placeholder="Ex: Suporte, IA, Comercial..."
                  class="w-full px-3 py-2 text-sm bg-n-background border border-n-weak rounded-lg outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 text-n-slate-12 placeholder:text-n-slate-8"
                  @keydown.enter="saveTicketType"
                />
              </div>
              <div class="flex-shrink-0">
                <label
                  class="block text-[10px] font-medium text-n-slate-10 mb-1"
                  >Cor</label>
                <input
                  v-model="typeForm.color"
                  type="color"
                  class="size-9 rounded-lg border border-n-weak cursor-pointer bg-n-background p-0.5"
                />
              </div>
              <button
                class="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-n-brand text-white hover:bg-n-brand/90 transition-colors disabled:opacity-50 flex-shrink-0"
                :disabled="!typeForm.name.trim() || isSavingType"
                @click="saveTicketType"
              >
                <span
                  v-if="isSavingType"
                  class="i-lucide-loader-circle size-4 animate-spin"
                />
                <span v-else class="i-lucide-check size-4" />
                {{ editingType ? 'Salvar' : 'Criar' }}
              </button>
              <button
                v-if="editingType || typeForm.parent_id"
                class="px-3 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors flex-shrink-0"
                @click="closeTypeForm"
              >
                Cancelar
              </button>
            </div>
          </div>

          <!-- Árvore de tipos -->
          <div
            v-if="!ticketTypes.length"
            class="flex flex-col items-center gap-2 py-10 text-n-slate-9"
          >
            <span class="i-lucide-git-branch size-10" />
            <p class="text-sm">Nenhum tipo cadastrado ainda.</p>
            <p class="text-xs text-n-slate-8">
              Use o formulário acima para criar o primeiro tipo.
            </p>
          </div>

          <div v-else class="space-y-1">
            <TypeTreeNode
              v-for="node in ticketTypes"
              :key="node.id"
              :node="node"
              :depth="0"
              :delete-confirm-id="typeDeleteConfirmId"
              :on-edit="openTypeForm"
              :on-add-child="id => openTypeForm(id)"
              :on-delete="
                id => {
                  typeDeleteConfirmId = id;
                }
              "
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirm para tipo -->
    <div
      v-if="typeDeleteConfirmId"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      @click.self="typeDeleteConfirmId = null"
    >
      <div
        class="bg-n-background border border-n-weak rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4"
      >
        <div class="flex items-center gap-3 mb-4">
          <span class="i-lucide-triangle-alert size-5 text-red-500" />
          <h3 class="text-base font-semibold text-n-slate-12">Excluir tipo?</h3>
        </div>
        <p class="text-sm text-n-slate-10 mb-5">
          Todos os filhos também serão excluídos. Tickets vinculados perderão o
          tipo.
        </p>
        <div class="flex gap-2 justify-end">
          <button
            class="px-4 py-2 rounded-lg text-sm border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
            @click="typeDeleteConfirmId = null"
          >
            Cancelar
          </button>
          <button
            class="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 transition-colors"
            @click="deleteTicketType(typeDeleteConfirmId)"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
